import { useCallback, useEffect, useMemo, useState } from "react";
import { useApplication } from "../../../app/providers/ApplicationProvider";
import type { Order } from "../../../domain/entities/Order";
import type { PaymentMethod, PaymentMethodField } from "../../../domain/entities/PaymentMethod";
import type { PaymentTransaction } from "../../../domain/entities/PaymentTransaction";
import { checkoutCopy } from "../../i18n/checkout/es";
import { useNavigation } from "../../routes/NavigationContext";

const PAYMENT_MOBILE_CODE = "PRW-PAYMENT-MOBILE";
const BINANCE_CODE = "PRW-BINANCE";
const BANK_OPTIONS = [
  "Banco Mercantil",
  "Banco de Venezuela",
  "Venezolano de Credito",
  "Banco Provincial",
  "Banco Del Caribe",
  "Banco Exterior",
  "Banco Caroni",
  "Banesco Banco Universal",
  "Banco Sofitasa Banco Universal",
  "Banco Plaza",
  "Banco De La Gente Emprendedora Bangente",
  "Banco Fondo Comun",
  "100% Banco",
  "Del Sur",
  "Banco Del Tesoro",
  "Banco Agricola De Venezuela",
  "Bancrecer",
  "R4",
  "Banco Activo",
  "Banplus",
  "Banco Nacional De Credito",
  "Bancamiga",
  "Banco Digital De Los Trabajadores",
  "Banco De La Fuerza Armada Nacional Bolivariana",
  "N58 Banco Digital",
];

const METHOD_SPECIFIC_FIELDS: Record<string, PaymentMethodField[]> = {
  [PAYMENT_MOBILE_CODE]: [
    {
      id: "dni",
      label: checkoutCopy.payment.fields.dniLabel,
      type: "select",
      required: true,
      placeholder: "",
      options: [
        { value: "V", label: "V" },
        { value: "J", label: "J" },
        { value: "E", label: "E" },
        { value: "P", label: "P" },
      ],
    },
    {
      id: "dni_number",
      label: checkoutCopy.payment.fields.dniNumberLabel,
      type: "text",
      required: true,
      placeholder: checkoutCopy.payment.fields.dniNumberPlaceholder,
    },
    {
      id: "phone_prefix",
      label: checkoutCopy.payment.fields.phonePrefixLabel,
      type: "select",
      required: true,
      placeholder: checkoutCopy.payment.fields.phonePrefixPlaceholder,
      options: [
        { value: "0424", label: "0424" },
        { value: "0414", label: "0414" },
        { value: "0412", label: "0412" },
        { value: "0416", label: "0416" },
        { value: "0426", label: "0426" },
      ],
    },
    {
      id: "phone_number",
      label: checkoutCopy.payment.fields.phoneNumberLabel,
      type: "tel",
      required: true,
      placeholder: checkoutCopy.payment.fields.phonePlaceholder,
    },
    {
      id: "bank",
      label: checkoutCopy.payment.fields.bankLabel,
      type: "text",
      required: true,
      placeholder: checkoutCopy.payment.fields.bankPlaceholder,
      options: BANK_OPTIONS.map((name) => ({ value: name, label: name })),
    },
    {
      id: "reference_number",
      label: checkoutCopy.payment.fields.referenceLabel,
      type: "text",
      required: true,
      placeholder: checkoutCopy.payment.fields.referencePlaceholder,
    },
  ],
  [BINANCE_CODE]: [
    {
      id: "email",
      label: checkoutCopy.payment.fields.emailLabel,
      type: "email",
      required: true,
      placeholder: checkoutCopy.payment.fields.emailPlaceholder,
    },
  ],
};

interface CheckoutState {
  order: Order | null;
  paymentMethods: PaymentMethod[];
  selectedPaymentMethodCode: string | null;
  selectedPaymentMethod: PaymentMethod | null;
  paymentFormValues: Record<string, string>;
  paymentFormFields: PaymentMethodField[];
  voucherFile: File | null;
  selectPaymentMethod: (paymentMethodId: string) => void;
  updatePaymentFormValue: (fieldId: string, value: string) => void;
  updateVoucherFile: (file: File | null) => void;
  completePayment: (tokenOverride?: string) => Promise<void>;
  isLoading: boolean;
  isProcessing: boolean;
  loadError: string | null;
  actionError: string | null;
  exchangeRate: number | null;
  isRateLoading: boolean;
  rateError: string | null;
}

type FormValuesByMethod = Record<string, Record<string, string>>;

export const useCheckoutState = (): CheckoutState => {
  const {
    getOrderById,
  getPaymentMethodsByCountry,
  getLatestTransactionByReferenceCode,
  processPayment,
  uploadPaymentVoucher,
  getExchangeRate,
  session,
} = useApplication();
  const { route, navigate } = useNavigation();
  const [order, setOrder] = useState<Order | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodCode, setSelectedPaymentMethodCode] = useState<string | null>(null);
  const [formValuesByMethod, setFormValuesByMethod] = useState<FormValuesByMethod>({});
  const [voucherFile, setVoucherFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [existingTransaction, setExistingTransaction] = useState<PaymentTransaction | null>(null);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [isRateLoading, setIsRateLoading] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);
  useEffect(() => {
    let isMounted = true;

    const loadCheckoutData = async () => {
      try {
        const [fetchedOrder, fetchedPaymentMethods, fetchedTransaction] = await Promise.all([
          getOrderById.execute(session.orderId),
          getPaymentMethodsByCountry.execute(session.countryCode),
          getLatestTransactionByReferenceCode.execute(session.orderId),
        ]);

        if (!isMounted) return;

        setOrder(fetchedOrder);
        setPaymentMethods(fetchedPaymentMethods);
        setExistingTransaction(fetchedTransaction);

        const firstMethod = fetchedPaymentMethods[0];
        setSelectedPaymentMethodCode(firstMethod?.code ?? null);
        setFormValuesByMethod((prev) => {
          if (!firstMethod) {
            return prev;
          }

          const fields = resolveFieldsForMethod(firstMethod);
          const initialValues = buildInitialValues(fields);
          const currentValues = prev[firstMethod.code];

          if (!currentValues) {
            return {
              ...prev,
              [firstMethod.code]: initialValues,
            };
          }

          const mergedValues = { ...initialValues, ...currentValues };

          return {
            ...prev,
            [firstMethod.code]: mergedValues,
          };
        });
        setVoucherFile(null);
        setIsLoading(false);
        setLoadError(null);
      } catch (err) {
        if (!isMounted) return;
        setLoadError(err instanceof Error ? err.message : checkoutCopy.order.unexpectedLoadError);
        setIsLoading(false);
        setExistingTransaction(null);
      }
    };

     loadCheckoutData()

    return () => {
      isMounted = false;
    };
  }, [
    getLatestTransactionByReferenceCode,
    getOrderById,
    getPaymentMethodsByCountry,
    session.countryCode,
    session.orderId,
  ]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (!existingTransaction || route.name !== "checkout") {
      return;
    }

    const relatedMethod =
      existingTransaction.paymentMethodCode != null
        ? paymentMethods.find((method) => method.code === existingTransaction.paymentMethodCode)
        : undefined;

    const paymentMethodName =
      relatedMethod?.name ?? existingTransaction.paymentMethodName ?? checkoutCopy.status.unknownMethodName;

    const status = normalizeTransactionStatus(existingTransaction.status);
    if (status === "pending") {
      navigate({
        name: "processing",
        transaction: existingTransaction,
        paymentMethod: relatedMethod,
        paymentMethodName,
        bookingCode: order?.bookingCode ?? order?.code,
      });
      return;
    }

    if (status === "success" && order) {
      navigate({
        name: "success",
        transaction: existingTransaction,
        order,
        paymentMethod: relatedMethod ?? null,
        paymentMethodName,
      });
    }
  }, [existingTransaction, isLoading, navigate, order, paymentMethods, route]);

  const selectPaymentMethod = useCallback(
    (paymentMethodId: string) => {
      setSelectedPaymentMethodCode(paymentMethodId);
      setActionError(null);

      const method = paymentMethods.find((item) => item.code === paymentMethodId);
      if (!method) {
        return;
      }

      setFormValuesByMethod((prev) => {
        const fields = resolveFieldsForMethod(method);
        const initialValues = buildInitialValues(fields);
        const currentValues = prev[paymentMethodId];

        if (!currentValues) {
          return {
            ...prev,
            [paymentMethodId]: initialValues,
          };
        }

        const mergedValues = { ...initialValues, ...currentValues };

        return {
          ...prev,
          [paymentMethodId]: mergedValues,
        };
      });

      setVoucherFile(null);
    },
    [paymentMethods],
  );

  const selectedPaymentMethod = useMemo(
    () => paymentMethods.find((method) => method.code === selectedPaymentMethodCode) ?? null,
    [paymentMethods, selectedPaymentMethodCode],
  );

  const paymentFormFields = useMemo(() => {
    if (!selectedPaymentMethod) {
      return [];
    }

    return resolveFieldsForMethod(selectedPaymentMethod);
  }, [selectedPaymentMethod]);

  const paymentFormValues = useMemo(() => {
    if (!selectedPaymentMethodCode) {
      return {};
    }

    return formValuesByMethod[selectedPaymentMethodCode] ?? {};
  }, [formValuesByMethod, selectedPaymentMethodCode]);

  const updatePaymentFormValue = useCallback(
    (fieldId: string, value: string) => {
      if (!selectedPaymentMethodCode) {
        return;
      }

      setFormValuesByMethod((prev) => ({
        ...prev,
        [selectedPaymentMethodCode]: {
          ...(prev[selectedPaymentMethodCode] ?? {}),
          [fieldId]: value,
        },
      }));
    },
    [selectedPaymentMethodCode],
  );

  const updateVoucherFile = useCallback((file: File | null) => {
    setVoucherFile(file);
    setActionError(null);
  }, []);

  const completePayment = useCallback(async (tokenOverride?: string) => {
    if (!order || !selectedPaymentMethod) {
      setActionError(checkoutCopy.payment.selectMethodValidation);
      return;
    }

    const missingRequiredField = paymentFormFields.find((field) => {
      if (!field.required) {
        return false;
      }

      const rawValue = paymentFormValues[field.id] ?? "";
      return rawValue.trim().length === 0;
    });

    if (missingRequiredField) {
      setActionError(checkoutCopy.payment.missingField(missingRequiredField.label));
      return;
    }

    const requiresVoucher = Boolean(selectedPaymentMethod.voucher?.required);

    if (requiresVoucher && !voucherFile) {
      setActionError(checkoutCopy.payment.voucherRequired);
      return;
    }

    const isPaymentMobile = selectedPaymentMethod.code === PAYMENT_MOBILE_CODE;
    const mobileMetadata = buildPaymentMobileMetadata({
      isPaymentMobile,
      exchangeRate,
      orderAmount: order.amount.amount,
    });

    if (isPaymentMobile && !mobileMetadata) {
      setActionError(checkoutCopy.payment.exchangeRateError);
      return;
    }

    try {
      setIsProcessing(true);
      setActionError(null);
      const transaction = await processPayment.execute({
        orderId: order.id,
        amount: order.amount,
        method: selectedPaymentMethod,
        currency: resolveCurrency(selectedPaymentMethod.code),
        token: tokenOverride ?? session.rawToken,
        metadata: {
          ...mapMetadata(paymentFormValues),
          ...(mobileMetadata ?? {}),
        },
      });

      if (requiresVoucher && voucherFile) {
        await uploadPaymentVoucher.execute({
          paymentMethodCode: selectedPaymentMethod.code,
          transactionCode: transaction.code,
          file: voucherFile,
          email: order.clientEmail,
        });
      }

      const status = normalizeTransactionStatus(transaction.status);
      const paymentMethodName = selectedPaymentMethod.name ?? checkoutCopy.status.unknownMethodName;

      if (status === "pending") {
        setExistingTransaction(transaction);
        navigate({
          name: "processing",
          transaction,
          paymentMethod: selectedPaymentMethod,
          paymentMethodName,
          bookingCode: order?.bookingCode ?? order?.code,
        });
        return;
      }

      if (status === "success") {
        setExistingTransaction(transaction);
        navigate({
          name: "success",
          transaction,
          order,
          paymentMethod: selectedPaymentMethod,
          paymentMethodName,
        });
        return;
      }

      setActionError(checkoutCopy.payment.unexpectedStatus);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : checkoutCopy.payment.genericError);
    } finally {
      setIsProcessing(false);
    }
  }, [
    exchangeRate,
    navigate,
    order,
    paymentFormFields,
    paymentFormValues,
    processPayment,
    selectedPaymentMethod,
    session.rawToken,
    uploadPaymentVoucher,
    voucherFile,
  ]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchRate = async () => {
      if (!selectedPaymentMethod || selectedPaymentMethod.code !== PAYMENT_MOBILE_CODE) {
        setExchangeRate(null);
        setRateError(null);
        setIsRateLoading(false);
        return;
      }

      setIsRateLoading(true);
      setRateError(null);

      try {
        const rate = await getExchangeRate.execute();
        if (!isMounted) return;
        setExchangeRate(rate);
      } catch (error) {
        if (!isMounted) return;
        setExchangeRate(null);
        setRateError(checkoutCopy.payment.exchangeRateError);
      } finally {
        if (isMounted) {
          setIsRateLoading(false);
        }
      }
    };

    void fetchRate();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [getExchangeRate, selectedPaymentMethod]);

  return {
    order,
    paymentMethods,
    selectedPaymentMethodCode,
    selectedPaymentMethod,
    paymentFormValues,
    paymentFormFields,
    voucherFile,
    selectPaymentMethod,
    updatePaymentFormValue,
    updateVoucherFile,
    completePayment,
    isLoading,
    isProcessing,
    loadError,
    actionError,
    exchangeRate,
    isRateLoading,
    rateError,
  };
};

const buildInitialValues = (fields: PaymentMethodField[]): Record<string, string> => {
  return fields.reduce<Record<string, string>>((acc, field) => {
    if (field.id === "dni") {
      acc[field.id] = "V";
      return acc;
    }

    if (field.id === "phone_prefix") {
      acc[field.id] = "0424";
      return acc;
    }

    acc[field.id] = "";
    return acc;
  }, {});
};

const mapMetadata = (values: Record<string, string>): Record<string, string | number> => {
  const metadata = Object.entries(values).reduce<Record<string, string | number>>((acc, [key, value]) => {
    const trimmed = value.trim();

    if (trimmed.length > 0) {
      acc[key] = trimmed;
    }

    return acc;
  }, {});

  const phonePrefix = metadata.phone_prefix ?? values.phone_prefix?.trim();
  const phoneNumber = metadata.phone_number ?? values.phone_number?.trim();

  if (phonePrefix && phoneNumber) {
    metadata.phone = `${phonePrefix}${phoneNumber}`;
  }

  delete metadata.phone_prefix;
  delete metadata.phone_number;

  const dniPrefix = metadata.dni ?? values.dni?.trim();
  const dniNumber = metadata.dni_number ?? values.dni_number?.trim();

  if (dniPrefix && dniNumber) {
    metadata.dni = `${dniPrefix}${dniNumber}`;
  }

  delete metadata.dni_number;

  return metadata;
};

const buildPaymentMobileMetadata = ({
  isPaymentMobile,
  exchangeRate,
  orderAmount,
}: {
  isPaymentMobile: boolean;
  exchangeRate: number | null;
  orderAmount: number;
}): Record<string, number> | null => {
  if (!isPaymentMobile) {
    return null;
  }

  if (exchangeRate === null || Number.isNaN(exchangeRate)) {
    return null;
  }

  const amountLocal = orderAmount * exchangeRate;

  if (!Number.isFinite(amountLocal)) {
    return null;
  }

  return {
    amount_local: amountLocal,
    exchange_rate: exchangeRate,
  };
};

const resolveFieldsForMethod = (method: PaymentMethod): PaymentMethodField[] => {
  const map = new Map<string, PaymentMethodField>();

  (method.configuration?.fields ?? []).forEach((field) => {
    map.set(field.id, { ...field });
  });

  const methodSpecific = METHOD_SPECIFIC_FIELDS[method.code] ?? [];

  methodSpecific.forEach((field) => {
    if (!map.has(field.id)) {
      map.set(field.id, { ...field });
    }
  });

  return Array.from(map.values());
};

const resolveCurrency = (methodCode: string): string => {
  if (methodCode === PAYMENT_MOBILE_CODE) {
    return "VES";
  }

  return "USD";
};

type TransactionStatus = "pending" | "success" | "unknown";

const normalizeTransactionStatus = (status: string): TransactionStatus => {
  const normalized = status.trim().toUpperCase();
  console.log("Estado normalizado:", normalized);

  if (normalized === "PENDING") {
    return "pending";
  }

  if (["PAID", "APPROVED", "COMPLETED", "SUCCESS", "CONFIRMED"].includes(normalized)) {
    return "success";
  }

  return "unknown";
};
